import { useState } from 'react'
import { useQueries } from 'react-query'
import { TimeFrame } from '../constants'
import { fetchPOSTRequest } from '../utils/network-adapter/fetch-request'
import { getSlugifiedApi } from '@/stories/strapi-4/utils/add-slugs-to-api'
import { TStrapiV4PageType } from '@/stories/strapi-4/common-types'

const fetchApiData = async (meta, slugsArr) => {
  if (!meta) {
    throw new Error('fetchApiData: meta is null or undefined')
  }

  const apiUrl = getSlugifiedApi(meta.api_url, slugsArr)

  const parsedUrl = new URL(apiUrl)

  if (meta.params) {
    for (const [key, value] of Object.entries(meta.params)) {
      parsedUrl.searchParams.set(key, value as string)
    }
  }

  try {
    const response = await fetchPOSTRequest({
      method: meta.api_type,
      url: parsedUrl,
      headers: {
        platform: 'web',
      },
      data: meta.payload ?? null,
    })

    if (!response) {
      throw new Error(
        `fetchApiData: Request failed with status ${response.status}`,
      )
    }

    return response
  } catch (error) {
    throw new Error(
      `fetchApiData: Failed to fetch data from ${apiUrl}: ${error.message}`,
    )
  }
}

const getNestedProperty = (data, path: string) => {
  const pathKeys = path.split('.')

  let result = data
  for (const key of pathKeys) {
    if (result?.hasOwnProperty(key)) {
      result = result[key]
    } else {
      result = data
      break
    }
  }
  return result
}

const processApiData = (apiData: TStrapiV4PageType, resp, meta) => {
  apiData.data.attributes.content.forEach(widget => {
    if (
      widget.__component === 'widgets.dynamic-place-holder-v0' &&
      widget.data_source?.api_identifier === meta.api_identifier &&
      resp
    ) {
      if (widget.data_source?.api_data_path) {
        const result = getNestedProperty(resp, widget.data_source.api_data_path)
        if (result) widget.data_source.api = result
      } else if (widget.data_source) {
        widget.data_source.api = resp
      }
    }
  })
  return apiData
}

const useFetchStrapiSourceData = (
  apiData: TStrapiV4PageType | null,
  slugsArr?: Array<string>,
  shouldRefetch?: boolean,
  refetchTimeInterval?: number,
) => {
  const dataSourceMap =
    apiData?.data.attributes.render_meta?.data_source_map ?? []
  const cloneStrapiApiData = JSON.parse(JSON.stringify(apiData))

  const [updatedApiData, setUpdatedApiData] = useState(cloneStrapiApiData)

  const queryResults = useQueries(
    dataSourceMap.map(meta => ({
      queryKey: [`apiData-${meta.api_identifier}`, meta.api_identifier],
      queryFn: () => fetchApiData(meta, slugsArr),
      onSuccess: resp => {
        const newClientData = processApiData(cloneStrapiApiData, resp, meta)
        setUpdatedApiData(newClientData)
      },
      onError: error => console.error('API fetch error:', error),
      refetchInterval: shouldRefetch
        ? refetchTimeInterval ?? TimeFrame.FIVE_MINUTES
        : Infinity,
    })),
  )

  if (queryResults.some(result => result.isError)) {
    console.info('API fetch error on client side:', queryResults)
    return null
  }

  return updatedApiData
}

export default useFetchStrapiSourceData
